import { useEffect, useState } from "react";

import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { DropdownTrigger } from "./components/DropdownTrigger";
import { ParentOption } from "./components/ParentOption";
import { type NestedMultiselectDropdownProps } from "./types";
import { useNestedDropdown } from "./useNestedDropdown";

export const NestedMultiselectDropdown = ({
  childPopoverContentProps,
  triggerClassName,
  options,
  placeholder = "Select",
  selectedOptions,
  setSelectedOptions,
  hideParentCheckboxes = false,
  disabled,
  isLoading,
  renderOptionLabel,
  renderSubOptionLabel,
  label,
  labelTag,
  name,
}: NestedMultiselectDropdownProps) => {
  const [open, setOpen] = useState(false);
  const {
    searchFilters,
    setSearchFilters,
    isOptionSelected,
    getSelectedCountForParent,
    handleOptionSelect,
    handleParentSelect,
    filterSubOptions,
    selectedParent,
    setSelectedParent,
  } = useNestedDropdown(options, selectedOptions, setSelectedOptions);

  const getTotalSelectedCount = () => selectedOptions.length;

  // Reset selected parent when dropdown is closed
  useEffect(() => {
    if (!open) {
      setSelectedParent(null);
    }
  }, [open, setSelectedParent]);

  return (
    <div>
      {isTruthy(label) && <FormLabel label={label} tag={labelTag} />}

      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger className={triggerClassName}>
          <DropdownTrigger
            className={cnTwMerge(isLoading && "skeleton-box")}
            dataTestId={`${name}-dropdown`}
            disabled={isLoading || disabled}
            isOpen={open}
            onOpenChange={setOpen}
            placeholder={placeholder}
            selectedCount={getTotalSelectedCount()}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="min-w-[8rem] max-w-nested-dropdown p-0 max-h-96 overflow-y-auto overflow-x-hidden text-text/primary rounded-companyBorderRadius"
          data-testid="nested-multiselect-popover"
        >
          <div className="relative flex flex-col">
            {options.map((option, index) => {
              const hasSubOptions =
                option.subOptions && option.subOptions.length > 0;
              const allSubOptionsSelected =
                hasSubOptions &&
                option.subOptions!.every((subOpt) =>
                  isOptionSelected(option.value, subOpt.value),
                ) &&
                option.subOptions!.length > 0;

              const selectedCount = hasSubOptions
                ? getSelectedCountForParent(option.value)
                : 0;

              const filteredSubOptions = filterSubOptions(option);

              return (
                <ParentOption
                  allSubOptionsSelected={allSubOptionsSelected}
                  childPopoverContentProps={childPopoverContentProps}
                  filteredSubOptions={filteredSubOptions}
                  hideCheckbox={hideParentCheckboxes}
                  index={index}
                  isOptionSelected={(optionValue) =>
                    isOptionSelected(option.value, optionValue)
                  }
                  isSelected={isOptionSelected(option.value, option.value)}
                  key={option.value}
                  onSelect={(checked) => handleParentSelect(option, checked)}
                  onSelectOption={(subOption) =>
                    handleOptionSelect(option.value, subOption)
                  }
                  option={option}
                  renderLabel={option.renderLabel || renderOptionLabel}
                  renderSubOptionLabel={renderSubOptionLabel}
                  searchValue={searchFilters[option.value] || null}
                  selectedCount={selectedCount}
                  selectedParent={selectedParent}
                  setSearchValue={(value) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      [option.value]: value,
                    }))
                  }
                  setSelectedParent={setSelectedParent}
                  totalOptions={options.length}
                />
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
